import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Zoom,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  createTheme,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Timeline as TimelineIcon,
  Psychology as PsychologyIcon,
  Insights as InsightsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Custom theme with modern design
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },
    secondary: {
      main: "#9c27b0",
      light: "#ba68c8",
      dark: "#7b1fa2",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
    },
    warning: {
      main: "#ed6c02",
      light: "#ff9800",
      dark: "#e65100",
    },
    error: {
      main: "#d32f2f",
      light: "#f44336",
      dark: "#c62828",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "3.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2.5rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: "1.25rem",
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 12,
          padding: "12px 24px",
        },
      },
    },
  },
});

const InsightsAnalysisHomePage = () => {
  const navigate = useNavigate();
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  const analysisOptions = [
    {
      id: "sast-vs-sast",
      title: "SAST Report Analysis",
      subtitle: "Static Application Security Testing Comparison",
      description:
        "Compare two SAST scan runs to analyze security improvements, track vulnerability remediation, and identify trends in your static code analysis.",
      color: "#667eea",
      features: [
        "Vulnerability trend analysis",
        "Remediation effectiveness tracking",
        "False positive rate comparison",
        "Security posture improvement metrics",
      ],
      gradient: "linear-gradient(135deg, #999999 0%, #999999 100%)",
      //   icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      //   stats: { accuracy: "99.2%", coverage: "95%", speed: "10x faster" },
      //   badge: "Most Popular",
      //   badgeColor: "success",
    },
    {
      id: "dast-vs-dast",
      title: "DAST Report Analysis",
      subtitle: "Dynamic Application Security Testing Comparison",
      description:
        "Compare dynamic security testing results to evaluate runtime security improvements and identify new attack vectors in your running applications.",
      color: "#f093fb",
      features: [
        "Runtime vulnerability analysis",
        "Attack surface comparison",
        "Response time security metrics",
        "Dynamic threat assessment",
      ],
      gradient: "linear-gradient(135deg, #999999 0%, #999999 100%)",
      //   icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      //   stats: { accuracy: "97.8%", coverage: "92%", speed: "8x faster" },
      //   badge: "Coming Soon",
      //   badgeColor: "warning",
    },
    // {
    //   id: "sast-vs-dast",
    //   title: "SAST vs DAST Analysis",
    //   subtitle: "Static vs Dynamic Security Testing Analysis",
    //   description:
    //     "Cross-compare static and dynamic security testing results to get a comprehensive view of your application security from multiple perspectives.",
    //   color: "#4facfe",
    //   features: [
    //     "Comprehensive security coverage",
    //     "Static vs dynamic correlation",
    //     "Holistic risk assessment",
    //     "Multi-dimensional analysis",
    //   ],
    //   gradient: "linear-gradient(135deg, #999999 0%, #999999 100%)",
    //   //   icon: <CompareIcon sx={{ fontSize: 48 }} />,
    //   //   stats: { accuracy: "98.5%", coverage: "98%", speed: "12x faster" },
    //   //   badge: "Advanced",
    //   //   badgeColor: "primary",
    // },
  ];

  const keyFeatures = [
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      title: "Precision Analysis",
      description:
        "Advanced AI algorithms provide accurate vulnerability assessment and risk prioritization",
      color: "#1976d2",
    },
    {
      icon: <InsightsIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      title: "Deep Insights",
      description:
        "Comprehensive reporting with actionable recommendations and trend analysis.",
      color: "#9c27b0",
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40, color: "#2e7d32" }} />,
      title: "Continuous Improvement",
      description:
        "Track security posture improvements and measure remediation effectiveness over time.",
      color: "#2e7d32",
    },
  ];

  const handleAnalysisSelect = (analysisType) => {
    setSelectedAnalysis(analysisType);
    // Add your navigation logic here
    console.log("Selected analysis type:", analysisType);
    if (analysisType === "sast-vs-sast") {
      navigate("/dashboard/security-testing/sast-insights-input");
    } else if (analysisType === "dast-vs-dast") {
      navigate("/dashboard/security-testing/dast-insights-input");
    } else {
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f6f3f2" }}>
      {/* Hero Section */}
      {/* <Box
        sx={{
          p: { xs: 2, md: 2 },
          display: "flex",
          alignItems: "center",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          flexGrow: 1,
          minHeight: { xs: "auto", md: "100px" },
          backgroundColor: "#FFFFFF",
          textAlign: { xs: "center", md: "left" },
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: 26, md: 30 },
              fontWeight: 700,
            }}
          >
            SAST-AI Metrics Analyzer
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: 12, md: 16 },
              fontWeight: 400,
              mt: 1,
            }}
          >
            Advanced Security Intelligence Platform
          </Typography>
        </Box>
      </Box> */}

      {/* Heading & Description */}
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          pt: { xs: 2, md: 4 },
          mt: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 },
          backgroundColor: "#fff",
          borderRadius: "10px",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "26px", md: "30px" },
            textAlign: "center",
            mb: 2,
            fontWeight: 700,
          }}
        >
          Select Your Analysis Type
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "15px", md: "17px" },
            textAlign: "center",
            // mb: { xs: 4, md: 6 },
            maxWidth: 800,
            mx: "auto",
          }}
        >
          Choose the type of security analysis that best fits your needs. Each
          option provides unique insights and comprehensive metrics tailored to
          your security testing approach.
        </Typography>
      </Box>

      {/* Cards */}
      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 4, md: 8 } }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {analysisOptions.map((option, index) => (
            <Grid item xs={12} sm={6} lg={6} key={option.id}>
              {/* <Zoom in timeout={800 + index * 200}> */}
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "visible",
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-8px) scale(1.02)",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                  },
                  borderRadius: "10px",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, pb: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: 20, md: 23 },
                      color: "text.primary",
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      mb: 2,
                      fontSize: { xs: 14, md: 16 },
                    }}
                  >
                    {option.subtitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      // mb: 2,
                      lineHeight: 1.6,
                      fontSize: { xs: 14, md: 16 },
                    }}
                  >
                    {option.description}
                  </Typography>

                  <List dense>
                    {option.features.map((feature, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: option.gradient,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              color: "text.secondary",
                              fontSize: { xs: 13, md: 14 },
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: { xs: 14, md: 16 },
                    }}
                    onClick={() => handleAnalysisSelect(option.id)}
                  >
                    Start Analysis
                  </Button>
                </CardContent>
                {/* <CardActions sx={{ p: { xs: 3, md: 4 } }}>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: { xs: 14, md: 16 },
                    }}
                    onClick={() => handleAnalysisSelect(option.id)}
                  >
                    Start Analysis
                  </Button>
                </CardActions> */}
              </Card>
              {/* </Zoom> */}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Key Features */}
      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 4, md: 8 }, ml: 4 }}>
        <Grid
          container // ✅ make it `container`
          spacing={{ xs: 2, sm: 3, md: 4 }}
          sx={{
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: "#fff",
            borderRadius: "10px",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {keyFeatures.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 2,
                    borderRadius: 2,
                    background: (theme) => theme.palette.grey[100],
                    backgroundColor: alpha(feature.color, 0.1),
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: "text.primary",
                    fontSize: { xs: 18, sm: 20, md: 22 },
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    maxWidth: "100%",
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default InsightsAnalysisHomePage;
